import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import { QueryClient, QueryClientProvider } from 'react-query'
import { HelmetProvider } from 'react-helmet-async'

import App from './App.tsx'
import { store } from './store/index.ts'
import './assets/styles/index.css'

// ============================================================
// OSINT Easter Eggs - Console Messages
// ============================================================

// Welcome message for curious developers
console.log('%c🔍 Welcome to Nexus Global Solutions', 'font-size: 20px; font-weight: bold; color: #1a365d;');
console.log('%cIf you\'re seeing this, you\'re already on the right track...', 'font-size: 14px; color: #4a5568;');
console.log('%c', 'padding: 50px 100px; background: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48dGV4dCB4PSI1MCIgeT0iNTAiIGZvbnQtc2l6ZT0iNTAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPvCfkYE8L3RleHQ+PC9zdmc+) no-repeat;');

// Hidden hints in console groups
console.groupCollapsed('%c📋 System Information', 'color: #2b6cb0; font-weight: bold;');
console.log('Application Version: 2.1.3');
console.log('Build Number: NGS-BUILD-4521');
console.log('Environment: production');
console.log('API Endpoint: /api');
console.log('Internal API: /api/internal (requires authentication)');
console.log('Debug Endpoint: /api/debug/config?debug=true');
console.groupEnd();

console.groupCollapsed('%c🔐 Security Notice', 'color: #c53030; font-weight: bold;');
console.log('This application tracks user behavior for educational purposes.');
console.log('Session data is collected and stored for instructor review.');
console.log('For security concerns, contact: security@nexusglobal-consulting.com');
console.log('Bug bounty program: bugbounty@nexusglobal-consulting.com');
console.log('PGP Key: /pgp-key.txt');
console.groupEnd();

console.groupCollapsed('%c💡 Hints for the Curious', 'color: #38a169; font-weight: bold;');
console.log('1. Have you checked robots.txt?');
console.log('2. The .well-known directory might have something interesting...');
console.log('3. Try viewing the page source - developers leave notes!');
console.log('4. API endpoints sometimes reveal more than intended');
console.log('5. HTTP headers can be very informative');
console.log('6. Some employees have... inconsistencies');
console.log('7. Press Ctrl+Shift+D for a surprise');
console.log('8. The truth is hidden in plain sight: QmFzZTY0IGlzIHlvdXIgZnJpZW5k');
console.groupEnd();

// Hidden function that can be called from console
(window as any).nexusDebug = {
  version: '2.1.3',
  buildDate: '2024-03-15T14:32:00Z',
  showSecrets: () => {
    console.log('%c🎉 You found a hidden function!', 'color: #805ad5; font-size: 16px; font-weight: bold;');
    console.log('Here are some paths to explore:');
    console.log('- /api/internal/employees (requires header: X-API-Key)');
    console.log('- /api/debug/config?debug=true');
    console.log('- /api/v2/news/:slug?include_clues=true');
    console.log('- /admin/login (test: testadmin@nexusglobal.com)');
    console.log('- Check localStorage for session data');
    console.log('');
    console.log('Base64 decode this: TkdTLUFQSS1URVNULTIwMjQteEs5bVAybkw=');
    return 'Keep exploring! 🔍';
  },
  endpoints: {
    public: '/api/content',
    tracking: '/api/tracking',
    auth: '/api/auth',
    internal: '/api/internal',
    debug: '/api/debug',
    backup: '/api/backup'
  },
  hint: () => {
    console.log('%cTry: nexusDebug.showSecrets()', 'color: #d69e2e;');
    return 'What secrets do you seek?';
  }
};

// Easter egg - warn message that looks like an error but is actually a hint
console.warn('NEXUS-DEBUG-001: Development artifacts detected in production build. Check /api/debug for more information.');

// Fake "error" that's actually a clue
console.log('%c[REDACTED]', 'color: transparent; text-shadow: 0 0 5px rgba(0,0,0,0.5);', 'Internal endpoint exposed: /api/internal/users/export');

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </Provider>
    </HelmetProvider>
  </React.StrictMode>,
)
